import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { ChangeEvent, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteBin6LineIcon,
  EditLineIcon,
  Information2LineIcon,
  SortAscIcon,
  SortDescIcon,
  TranferIcon,
} from '~/assets/icons';
import { cast } from '~/utils/helper';
import { IAccount } from '~/utils/interface/account';
import styles from '../Distributor.module.scss';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { SEARCH_TIMEOUT } from '~/utils/constants/common';
dayjs.extend(customParseFormat);

const cx = classNames.bind(styles);

type Props = {
  pageSize: number;
  data: IAccount[];
  form: any;
  distributorIds?: Array<string>;
  handleCheck?: (row: IAccount, checked: boolean) => void;
  handleCheckAll?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onSetRefreshData: (refresh: boolean) => void;
  onDetail?: (row: IAccount) => void;
  onEdit?: (row: IAccount) => void;
  onTransfer?: (row: IAccount) => void;
  onDelete?: (row: IAccount) => void;
};

const DistributorTable = (props: Props) => {
  const { t } = useTranslation('admin/distributor');
  const { distributorIds, handleCheck, handleCheckAll } = props;

  const searchTimeout = useRef<any>(null);

  const handleSelectedOptions = (field: string, value: any) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      props.form.setFieldValue(field, value);
    }, SEARCH_TIMEOUT);
  };

  const handleSort = (field: string, type: string) => {
    props.form.setFieldValue('order_by', { field, type });
  };

  const getDistributorLevelNameColor = (level: number) => {
    if (level === 1) return 'border-[#72777B] text-[#72777B]';
    if (level === 2) return 'border-[#FF9500] text-[#FF9500]';
    if (level === 3) return 'border-[#3D9149] text-[#3D9149]';
    return 'border-[#005F73] text-[#005F73]';
  };
  const getDistributorLevelName = (level: number) => {
    return (
      <Box className={`border rounded-[20px] p-1 text-center h-[25px] ${getDistributorLevelNameColor(level)}`}>
        {t('distributorLevel.' + level)}
      </Box>
    );
  };

  return (
    <TableContainer
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: '1px',
        maxHeight: '100vh',
      }}
    >
      <Table stickyHeader>
        <TableHead className='bg-[#005F73]'>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                onChange={handleCheckAll}
                sx={{
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
              />
            </TableCell>
            <TableCell className={cx('w-[70px]', 'table-header')} align='center'>
              {t('table.no')}
            </TableCell>

            {/* username */}
            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.username')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'username' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('username', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('username', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.username}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('username', e.target.value)}
              />
            </TableCell>

            {/* name */}
            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.name')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'name' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('name', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('name', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.name}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('name', e.target.value)}
              />
            </TableCell>

            {/* address */}
            <TableCell className='text-white border-l border-white min-w-[250px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.address')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'address' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('address', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('address', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.address}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('address', e.target.value)}
              />
            </TableCell>

            {/* representative */}
            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.representative')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'representative' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('representative', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('representative', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.representative}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('representative', e.target.value)}
              />
            </TableCell>

            {/* phone */}
            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.phone')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'phone' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('phone', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('phone', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.phone}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('phone', e.target.value)}
              />
            </TableCell>

            {/* email */}
            <TableCell className='text-white border-l border-white min-w-[250px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.email')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'email' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('email', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('email', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.email}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('email', e.target.value)}
              />
            </TableCell>

            {/* total_agents */}
            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.total_agents')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'total_agents' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_agents', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_agents', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_agents}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('total_agents', e.target.value)}
              />
            </TableCell>

            {/* total_devices */}
            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.total_devices')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'total_devices' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_devices', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_devices', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_devices}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('total_devices', e.target.value)}
              />
            </TableCell>

            {/* total_users */}
            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.total_users')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'total_users' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_users', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_users', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_users}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('total_users', e.target.value)}
              />
            </TableCell>

            {/* created_at */}
            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.created_at')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'created_at' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('created_at', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('created_at', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.created_at}
                placeholder={t('search')}
                className='header-search-input'
                onChange={(e) => handleSelectedOptions('created_at', e.target.value)}
              />
            </TableCell>

            {/* distributor_level */}
            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.distributor_level')}</Box>
                {props.form.values.order_by.type === 'asc' &&
                props.form.values.order_by.field === 'distributor_level' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('distributor_level', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('distributor_level', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>

            <TableCell className={cx('min-w-[150px]', 'table-header')} align='center'>
              {t('table.action')}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {props.data.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={
                    (distributorIds ?? []).find((distributorId) => distributorId === row.username) ? true : false
                  }
                  onChange={(event, checked) => handleCheck && handleCheck(row, checked)}
                  sx={{
                    color: '#005F73',
                    '&.Mui-checked': {
                      color: '#005F73',
                    },
                  }}
                />
              </TableCell>
              <TableCell align='center'>{index + 1 + (props.form.values.page_index - 1) * props.pageSize}</TableCell>
              <TableCell align='center'>{row.username}</TableCell>
              <TableCell align='center'>{row.name}</TableCell>
              <TableCell align='center'>{row.address}</TableCell>
              <TableCell align='center'>{row.representative}</TableCell>
              <TableCell align='center'>{row.phone}</TableCell>
              <TableCell align='center'>{row.email}</TableCell>
              <TableCell align='center'>{row.total_agents}</TableCell>
              <TableCell align='center'>{row.total_devices}</TableCell>
              <TableCell align='center'>{row.total_users}</TableCell>
              <TableCell align='center'>{row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : ''}</TableCell>
              <TableCell align='center'>{getDistributorLevelName(cast(row.distributor_level))}</TableCell>
              <TableCell align='center'>
                <Box className='flex'>
                  <IconButton onClick={() => props?.onDetail && props.onDetail(row)}>
                    <Information2LineIcon fill='#72777B' />
                  </IconButton>
                  <IconButton onClick={() => props?.onEdit && props.onEdit(row)}>
                    <EditLineIcon />
                  </IconButton>
                  <IconButton onClick={() => props?.onTransfer && props.onTransfer(row)}>
                    <TranferIcon />
                  </IconButton>
                  {row.has_agents ? (
                    <IconButton onClick={() => props?.onTransfer && props.onTransfer(row)}>
                      <DeleteBin6LineIcon fill='#72777B' />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => props?.onDelete && props.onDelete(row)}>
                      <DeleteBin6LineIcon fill='#ED1002' />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(DistributorTable);
